import axios from "axios";
import { getRailsClient, getDirectoryClient } from "../client";
import { buildHeaderForUpload, getHeaders } from "../utils/headers";
import { isGuestAccessMode } from "../utils/guest";
import type { UserProfile } from "../client";

export interface UserRailsProfile {
  currentLevel: CurrentLevel;
  totalTime: number;
  lastMonthTotalTime: number;
  lastMonthPoints: number;
  totalPoints: number;
  gravatarId: string;
  smallImageUrl: string | null;
  thumbImageUrl: string | null;
  hasAccessToDojoActivities: boolean; // this is access to champions passport
}

interface CurrentLevel {
  id: number;
  levelName: string;
  statsCurrent: number;
  statsTotal: number;
  statsProgress: number;
  pathName: string;
}

export const fetchUserParticipationSettings = async () => {
  try {
    const API = `/api/internal_api/atlas/v1/current_user/participation`;
    const { data } = await getRailsClient().get(API);
    return data;
  } catch (error) {
    console.error("Error fetching user settings:", error);
  }
};

export const updateUserAttributes = async (name: string, value: any) => {
  const API = `/api/internal_api/v1/current_user/update`;

  try {
    const payload = { user: { [name]: value } };
    const response = await getRailsClient().put(API, payload);
    return response?.data?.user;
  } catch (error) {
    console.error("Error updating user attributes:", error);
    throw error;
  }
};

export const updateNameOnCertificate = async (certFirstName: string, certLastName: string) => {
  const API = `/api/internal_api/v1/current_user/update`;

  try {
    const payload = {
      user: {
        certFirstName,
        certLastName,
      },
      merge: false,
    };
    const response = await getRailsClient().put(API, payload);
    return response?.data?.user;
  } catch (error) {
    console.error("Error updating certificate name:", error);
    throw error;
  }
};

export const uploadUserImage = async (file: File): Promise<any> => {
  const API = "/api/internal_api/v1/current_user/update";

  try {
    const formData = new FormData();
    formData.append("user[image]", file);

    const response = await axios.put(API, formData, {
      headers: await buildHeaderForUpload(),
    });

    return response?.data?.user;
  } catch (error) {
    console.error("Error uploading user image:", error);
    throw error;
  }
};

export const fetchUserPathData = async () => {
  try {
    const API = `/api/internal_api/v1/current_user/show?filter[]=passed_belts&filter[]=user`;
    const { data } = await getRailsClient().get(API);

    const sortedPassedBelts = [...(data.passedBelts || [])].sort((a, b) => {
      const dateA = new Date(a.completedAt).getTime();
      const dateB = new Date(b.completedAt).getTime();

      // Push invalid dates to the end
      if (Number.isNaN(dateA) && Number.isNaN(dateB)) return 0;
      if (Number.isNaN(dateA)) return 1;
      if (Number.isNaN(dateB)) return -1;

      // Newest first
      return dateB - dateA;
    });

    return {
      user: data.user,
      passedBelts: sortedPassedBelts,
    };
  } catch (error) {
    console.error("Error fetching user path data:", error);
    throw error;
  }
};

export const downloadPathCertificate = async (roleId: number) => {
  const API = `/api/internal_api/atlas/v1/roles/${roleId}/certificates`;
  const headers = await getHeaders();
  const response = await axios.get(API, {
    responseType: "blob",
    headers,
  });

  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement("a");
  try {
    link.href = url;
    link.setAttribute("download", `certificate-${roleId}.pdf`);
    document.body.appendChild(link);
    link.click();
  } finally {
    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
};

export const fetchUserProfileData = async (): Promise<UserRailsProfile> => {
  try {
    const API = "/api/internal_api/atlas/v1/current_user/profile";
    const { data } = await getRailsClient().get(API);
    if (!data) {
      throw new Error("No user profile data found");
    }
    return data as UserRailsProfile;
  } catch (error) {
    console.error("Error fetching user stats:", error);
    throw error;
  }
};

export const fetchProfileAchievements = async () => {
  try {
    const ACHIEVEMENTS_API = "/api/internal_api/atlas/v1/current_user/achievements";
    const { data } = await getRailsClient().get(ACHIEVEMENTS_API);
    return data;
  } catch (error) {
    console.error("Error fetching profile achievements:", error);
  }
};

export const fetchUserVideoSettings = async () => {
  // Don't try to retrieve video settings in guest access mode
  if (isGuestAccessMode()) {
    return null;
  }

  try {
    const API = "/api/internal_api/v1/current_user/show?filter[]=user";
    const {
      data: {
        user: { videoSettings },
      },
    } = await getRailsClient().get(API);
    return videoSettings;
  } catch (error) {
    console.error("Error fetching user video settings:", error);
  }
};

export const updateUser = async (userProfile: Partial<UserProfile>) => {
  try {
    const client = getDirectoryClient();
    const { data, error } = await client.updateSelf(userProfile);

    if (error) {
      console.error("Error updating user profile:", error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error("Error updating user profile:", error);
    throw error;
  }
};

export const getPathByCollectionKey = async (collectionKey: string) => {
  try {
    const API = `/api/internal_api/atlas/v1/roles/${collectionKey}`;
    const { data } = await getRailsClient().get(API);
    return data;
  } catch (error) {
    console.error("Error fetching path by collection key:", error);
  }
};
